/**
* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import { useEffect } from 'react';
import { useShadowRootElements } from '@backstage/plugin-techdocs-react';
import { useLocation } from 'react-router-dom';

/**
* This Tech doc addon used to customize the docs page
* @returns
*/

export const CustomTechDocStyleAddon = () => {
  const location = useLocation();

  // existing specific selectors
  const sideMenuScrollBar = useShadowRootElements<HTMLAnchorElement>([
    "div[class='md-sidebar__scrollwrap']",
  ]);
  const techdocsHeaderIcons = useShadowRootElements<HTMLAnchorElement>([
    "a[title='Edit this page']",
    "a[title='Leave feedback for this page']",
    "a[data-md-component='source']",
  ]);

  // remove header icons (existing)
  useEffect(() => {
    if (techdocsHeaderIcons && techdocsHeaderIcons.length > 0) {
      techdocsHeaderIcons.forEach(icon => icon.remove());
    }
  }, [techdocsHeaderIcons]);

  useEffect(() => {
    if (sideMenuScrollBar && sideMenuScrollBar.length > 0) {
      sideMenuScrollBar.forEach(sideScrollBar => {
        sideScrollBar.style.height = '100%';
      });
    }
  }, [sideMenuScrollBar]);

  useEffect(() => {
    const scrollBarShiftToTop = document.querySelector("div#root main");
    if (scrollBarShiftToTop) {
      scrollBarShiftToTop?.scrollTo(0, 0);
    }

    const scrollBarShiftToTopDocs = document.querySelector('div#root main article main');
    if (scrollBarShiftToTopDocs) {
      scrollBarShiftToTopDocs.scrollTo(0, 0);
    }
  }, [location.pathname]);

  // Hide Owner and Source metadata in TechDocs header
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const hideOwnerAndSource = () => {
      // Strategy 1: Find elements with exact text "Owner" or "Source" and hide their parent containers
      const allElements = document.querySelectorAll('header *');

      allElements.forEach(el => {
        if (!(el instanceof HTMLElement)) return;

        const text = el.textContent?.trim();

        // If this element contains ONLY "Owner" or ONLY "Source" as text
        if (text === 'Owner' || text === 'Source') {
          // Find the parent container (Grid item or similar)
          let container = el.parentElement;

          // Walk up to find a suitable container (div with Grid class or small container)
          while (container && container !== document.body) {
            const className = container.className ?? '';

            // If we found a Grid item or a container div
            if (className.includes('Grid') || 
                (container.tagName === 'DIV' && container.children.length <= 3)) {
              container.style.display = 'none';
              break;
            }

            container = container.parentElement;
          }
        }
      });

      // Strategy 2: Find and hide owner links (bh-cordant-developer, etc.)
      const links = document.querySelectorAll('header a[href*="/catalog/"]');
      links.forEach(link => {
        if (link instanceof HTMLElement) {
          const parentContainer = link.closest('[class*="Grid"]') ?? link.parentElement;
          if (parentContainer instanceof HTMLElement) {
            // Check if this is in an Owner section by looking at siblings
            const allText = parentContainer.textContent ?? '';
            if (allText.toLowerCase().includes('owner') || allText.toLowerCase().includes('lifecycle')) {
              parentContainer.style.display = 'none';
            }
          }
        }
      });

      // Strategy 3: Remove link from "Documentation" breadcrumb
      const breadcrumbLinks = document.querySelectorAll('nav a, a[class*="Breadcrumb"]');
      breadcrumbLinks.forEach(link => {
        if (link instanceof HTMLAnchorElement && link.textContent?.trim() === 'Documentation') {
          link.style.pointerEvents = 'none';
          link.style.cursor = 'default';
          link.style.textDecoration = 'none';
          link.removeAttribute('href');
        }
      });
    };

    // Run multiple times to catch dynamic content
    const timer1 = setTimeout(hideOwnerAndSource, 100);
    const timer2 = setTimeout(hideOwnerAndSource, 500);
    const timer3 = setTimeout(hideOwnerAndSource, 1000);
    const timer4 = setTimeout(hideOwnerAndSource, 2000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(timer4);
    };
  }, [location.pathname]);

  // remove padding-top from techdocs reader / backstage content containers
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const removeMarginTop = () => {
      try {
        const selector = [
          '[class*="techdocs-reader-page"]',
          '[class*="TechDocsReaderPage"]',
          '.techdocs-reader-page',
          '.backstage-content',
          '[class*="BackstageContent"]',
        ].join(',');
        const elems = Array.from(document.querySelectorAll<HTMLElement>(selector));
        elems.forEach(e => {
          e.style.marginTop = '0px';
        });
      } catch {}
    };

    // run immediately and observe for late-rendered DOM
    removeMarginTop();
    const mo = new MutationObserver(removeMarginTop);
    mo.observe(document.body, { childList: true, subtree: true });
    const t = setTimeout(removeMarginTop, 500);

    return () => {
      mo.disconnect();
      clearTimeout(t);
    };
  }, [location.pathname]);

  // Fix double <article> padding when docs are opened from catalog entity page.
  // Catalog entity layout wraps content in an <article> with padding, and the
  // TechDocs MkDocs shadow DOM also renders an <article> — double padding breaks
  // the sidebar text overflowing outside its background.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    // Compute once per pathname change — if not a catalog docs page, do nothing at all.
    // This avoids attaching a MutationObserver on every page unnecessarily.
    const isCatalogDocsPage =
      window.location.pathname.includes('/catalog/') &&
      window.location.pathname.includes('/docs');

    if (!isCatalogDocsPage) return;

    const fixDoubleArticlePadding = () => {
      try {
        // Target the direct child <article> of main.techdocs-reader-page.
        // Based on actual DOM: main[class*="techdocs-reader-page"] > article
        // Only zero paddingLeft and paddingRight to fix horizontal overflow,
        // leaving top/bottom padding intact.
        const articles = document.querySelectorAll<HTMLElement>(
          'main[class*="techdocs-reader-page"] > article',
        );

        articles.forEach(article => {
          article.style.paddingLeft = '0';
          article.style.paddingRight = '0';
        });
      } catch {}
    };

    fixDoubleArticlePadding();

    // Observe only the techdocs reader container (narrower than document.body)
    // to avoid reacting to every DOM mutation on the page.
    const readerContainer = document.querySelector<HTMLElement>(
      'main[class*="techdocs-reader-page"]',
    );
    const target = readerContainer ?? document.body;
    const mo = new MutationObserver(fixDoubleArticlePadding);
    mo.observe(target, { childList: true, subtree: true });
    const t = setTimeout(fixDoubleArticlePadding, 500);

    return () => {
      mo.disconnect();
      clearTimeout(t);
    };
  }, [location.pathname]);

  useEffect(() => {
    if ((window as any).apiDocOpen) {
      window.location.reload();
    }
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const applyToShadowRoot = (sr: ShadowRoot | null) => {
      if (!sr) return;
      try {
        // inline fallback in case footer already exists and needs immediate update
        const foot = sr.querySelector<HTMLElement>('footer, .md-footer, .techdocs-footer');
        if (foot) foot.style.backgroundColor = 'var(--md-default-bg-color)';
      } catch {}
    };

    const scan = () => {
      const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-testid="techdocs-native-shadowroot"]'));
      nodes.forEach(n => applyToShadowRoot((n as any).shadowRoot ?? null));
    };

    // initial scan + observe DOM for dynamically added shadow root containers
    scan();
    const mo = new MutationObserver(muts => {
      for (const m of muts) {
        for (const n of Array.from(m.addedNodes)) {
          if (n instanceof HTMLElement && n.dataset?.testid === 'techdocs-native-shadowroot') {
            applyToShadowRoot((n as any).shadowRoot ?? null);
          }
        }
      }
      // also re-scan in case shadow roots were attached after node insertion
      scan();
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => mo.disconnect();
  }, []);

  return null;
};
